import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { PageContainer } from '../../modules/page-container/page-container';
import { FullPageLoader } from '../../visual-components/full-page-loader/full-page-loader';
import { GridContainer, GridItem } from '../../visual-components/grid/grid';
import { Link, LinkButton } from '../../visual-components/link/link';
import { Spacer } from '../../visual-components/spacer/spacer';
import { DefinitionList } from '../../visual-components/definition-list/definition-list';
import { DefinitionBasic } from '../../visual-components/definition-list/definition-renderers/definition-basic/definition-basic';
import { useSelfQuery } from '../../modules/user/queries';
import isNil from '../../core/helpers/is-nil';
import { Typography } from '../../visual-components/typography/typography';
import { LinkList } from '../../visual-components/link-list/link-list';
import { FullPageErrorView } from '../../modules/full-page-error-view/full-page-error-view';
import { ChangePasswordDialog } from './components/change-password-dialog';
import { OpenOrders } from './components/open-oders';
import { RecentArticles } from './components/recent-articles';

export const Overview: React.FC = () => {
  const { t } = useTranslation();
  const { profileSettingsPath } = usePathBuilder();
  const { logout } = useAuth0();

  const { data: selfData, isLoading: isLoadingSelf, error: selfError } = useSelfQuery();

  if (isLoadingSelf) {
    return <FullPageLoader />;
  }

  const self = selfData?.data;
  if (isNil(self) || selfError) {
    return <FullPageErrorView debugInfo="Error fetching user self data" error={selfError} />;
  }

  return (
    <PageContainer documentTitle={t('profile.documentTitle')}>
      <GridContainer>
        <GridItem>
          <Typography variant="headline-1">{t('profile.overview.title')}</Typography>
          <Spacer size="07" />
        </GridItem>
        <GridItem span="6">
          <DefinitionList title={t('profile.overview.userDetails')}>
            <DefinitionBasic term={t('global.name')}>{self.name}</DefinitionBasic>
            <DefinitionBasic term={t('global.email')}>{self.email}</DefinitionBasic>
            <DefinitionBasic term={t('profile.users.isAdmin')}>
              {self.isCustomerAdmin ? t('global.yes') : t('global.no')}
            </DefinitionBasic>
          </DefinitionList>
        </GridItem>
        <GridItem span="6">
          <LinkList>
            <Link icon="slide-control" to={profileSettingsPath()}>
              {t('profile.settings')}
            </Link>
            <ChangePasswordDialog email={self.email} />
            <LinkButton
              icon="box-half-arrow-out"
              onClick={() => {
                logout();
              }}
            >
              {t('global.logout')}
            </LinkButton>
          </LinkList>
        </GridItem>
      </GridContainer>
      <RecentArticles />
      <OpenOrders />
    </PageContainer>
  );
};
